/* THE APP-ON-OPEN FALLBACK FOR `apply-next-week` — and its whole surface is a decision, not a call.
 *
 * ⚠️ WHY A FALLBACK AND NOT A SCHEDULER, MEASURED RATHER THAN ASSUMED. `pg_net` and `pg_cron` are
 * NOT installed on this project (`CLAUDE.md`, "Live schema facts"), so no database trigger can
 * reach an edge function; and a GitHub-Actions cron would need his credentials, which are his.
 * That leaves the shape his 2026-09-10 design already named: the coach's own client screen asks,
 * when a coach opens it. The phone does the same thing.
 *
 * 🔴 F15 STEP 3.7 (2026-10-31): IT IS ON. The endpoint writes (its rules pinned to the Swift by
 * `progression-fixture.json`, its I/O the phone's statement for statement, as the coach), and a coach who lives on
 * the web — William, the coach beta's first real user, has no iPhone — otherwise never gets a week written at all.
 * It runs ONCE PER PAGE LOAD from the app shell, where a coach LANDS (the phone's runner sits on its dashboard read
 * for the same reason), never from one screen a coach may never open. It writes nothing on its own: the endpoint
 * writes only for a client whose `auto_apply_next_week` is ON and whose day is DUE, and a second call writes nothing.
 * ⚠️ WHAT IT CANNOT DO, said so it is not read as the whole design: a coach who opens neither the web nor the phone
 * in a week leaves that client on nothing (no scheduler: `pg_cron`/`pg_net` absent, MEASURED 2026-10-31).
 *
 * ⚠️ THE DECISION IS SEPARATED FROM THE CALL SO IT CAN BE TESTED AT ALL. A `useEffect` that both
 * decides and fetches is a rule nothing can execute without a network; `plannedCalls` is pure and
 * `runAutoApply` is the four lines that cannot be.
 */

/** 🔴 ON since F15 step 3.7 — see the header. */
export const AUTO_APPLY_ON_OPEN = true;

export interface RosterClientLike {
  clientId: string | null;
  status: string;
}

export interface AutoApplyCall {
  clientId: string;
  today: string;
}

/**
 * Which clients this screen would ask about, in a stable order.
 *
 * ⚠️ IT ASKS ONLY FOR **ACTIVE** LINKS WITH A REAL CLIENT ID, and that is not an optimisation —
 * `coach_clients` legitimately holds rows that predate the account (`invited_*` with a null
 * `client_id`), and asking about one would be asking the endpoint about nobody.
 *
 * ⚠️ IT DOES NOT ASK WHETHER THE TOGGLE IS ON, and that is deliberate: the toggle is the
 * ENDPOINT'S check, read in the same statement as the relationship. A screen that pre-filtered on
 * its own copy of `auto_apply_next_week` would be a second statement of the safety switch, and the
 * day the two disagree the screen is the one that decides — which is the wrong way round. The cost
 * is stated: one refused request per switched-off client, which is a 409 and nothing else.
 *
 * ⚠️ AND IT DE-DUPLICATES. A roster can legitimately show one client twice (a re-invitation beside
 * an active link); two calls for one client would be idempotent at the database
 * (`coach_assignments_one_source_per_day_idx`) and still two weeks' worth of reasoning run twice.
 */
export function plannedCalls(clients: RosterClientLike[], today: string): AutoApplyCall[] {
  const seen = new Set<string>();
  const out: AutoApplyCall[] = [];
  for (const c of clients) {
    if (c.status !== "active") continue;
    const id = c.clientId;
    if (id === null || id === "") continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ clientId: id, today });
  }
  return out;
}

export interface AutoApplyOutcome {
  clientId: string;
  /** The endpoint's HTTP status, or `null` when the request never completed. */
  status: number | null;
  /** The endpoint's own sentence, when it gave one. */
  message: string | null;
}

/**
 * Ask the endpoint, one client at a time.
 *
 * ⚠️ SEQUENTIAL, NOT PARALLEL, AND THE REASON IS NOT POLITENESS: each call may CREATE an
 * assignment, and `coach_assignments_one_source_per_day_idx` refuses a second copy of one root on
 * one day — so parallel calls for one coach turn a race into a 23505 that reads like a failure.
 * One at a time makes the second call see the first's work, which is what "idempotent by what it
 * reads" means.
 *
 * ⚠️ IT NEVER THROWS AND IT NEVER RETRIES. This is a background convenience on a screen a coach
 * opened to read something else; an error here must not take the roster down with it, and a retry
 * loop around a call that can write training is a way to write it twice.
 */
export async function runAutoApply(
  calls: AutoApplyCall[],
  invoke: (body: AutoApplyCall) => Promise<{ status: number; message: string | null }>,
): Promise<AutoApplyOutcome[]> {
  const out: AutoApplyOutcome[] = [];
  for (const call of calls) {
    try {
      const r = await invoke(call);
      out.push({ clientId: call.clientId, status: r.status, message: r.message });
    } catch (e) {
      out.push({
        clientId: call.clientId,
        status: null,
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }
  return out;
}

/** Once per page load per coach — the shell may re-render, a sign-in may repeat; the endpoint's own idempotence is the
 *  second guard, never the first. */
const ranFor = new Set<string>();

/** The app shell's call: the coach's links (as the coach, RLS), then `apply-next-week` for each ACTIVE one. */
export async function autoApplyOnOpen(
  coachId: string,
  today: string,
  listLinks: (coachId: string) => Promise<RosterClientLike[]>,
  invoke: (body: AutoApplyCall) => Promise<{ status: number; message: string | null }>,
): Promise<AutoApplyOutcome[]> {
  if (ranFor.has(coachId)) return [];
  ranFor.add(coachId);
  let links: RosterClientLike[];
  try { links = await listLinks(coachId); } catch { return []; }
  return runAutoApply(plannedCalls(links, today), invoke);
}

/** For the tests: forget which coaches this page load already ran for. */
export function resetAutoApplyOnOpen(): void { ranFor.clear(); }
